import { useEffect, useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from '@/context/AuthContext';
import { useDebounce } from '@/hooks/useDebounce';

const OrganizationStep = ({ stepKey, updateStepData, getStepData }) => {
    const { user, axiosInstance } = useAuth();
    const stepData = getStepData(stepKey);
    const choiceStep = getStepData('choice');
    const [slugError, setSlugError] = useState('');
    const [isCheckingSlug, setIsCheckingSlug] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(false);
    const debouncedSlug = useDebounce(stepData.slug, 500);

    useEffect(() => {
        if (stepData) {
            updateStepData(stepKey, stepData);
        }
    }, []);

    useEffect(() => {
        // Only check slug when creating new org
        if (choiceStep === 'create') {
            const checkSlugExists = async () => {
                if (!debouncedSlug) return;
                
                setIsCheckingSlug(true);
                try {
                    const response = await axiosInstance.get(`/organizations/check-slug/${debouncedSlug}`);
                    if (response.data.exists) {
                        setSlugError(`Organization URL "${debouncedSlug}" is already taken`);
                    } else {
                        setSlugError('');
                    }
                } catch (error) {
                    console.error('Error checking slug:', error);
                } finally {
                    setIsCheckingSlug(false);
                }
            };
            checkSlugExists();
        }
    }, [debouncedSlug, choiceStep]);

    useEffect(() => {
        // Fetch organizations when joining existing
        if (choiceStep === 'join') {
            const fetchOrganizations = async () => {
                setLoading(true);
                try {
                    const response = await axiosInstance.get('/organizations');
                    setOrganizations(response.data);
                } catch (error) {
                    console.error('Error fetching organizations:', error);
                } finally {
                    setLoading(false);
                }
            };
            fetchOrganizations();
        }
    }, [choiceStep]);

    const handleNameChange = (value) => {
        const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        updateStepData(stepKey, { 
            ...stepData, 
            name: value,
            slug
        });
    };

    if (choiceStep === 'join') {
        return (
            <div className="w-full max-w-xl mx-auto flex flex-col space-y-6">
                <div className="text-center">
                    <h1 className="font-semibold text-3xl">Join an Organization</h1>
                    <p className="text-muted-foreground">Select an organization to join from the list below.</p>
                </div>
                <div className="w-full">
                    {loading ? (
                        <div className="text-center text-muted-foreground">Loading organization invites...</div>
                    ) : organizations.length > 0 ? (
                        <div className="space-y-4">
                            {organizations.map(org => (
                                <button
                                    key={org.id}
                                    onClick={() => updateStepData(stepKey, { organizationId: org.id })}
                                    className={`w-full p-4 border rounded-lg text-left transition-all hover:border-primary ${
                                        stepData.organizationId === org.id ? 'border-primary bg-primary/5' : ''
                                    }`}
                                >
                                    <h3 className="font-medium">{org.name}</h3>
                                    <p className="text-sm text-muted-foreground">Created {new Date(org.createdAt).toLocaleDateString()}</p>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center space-y-3 py-4">
                            <p className="text-red-500">No organization invites found.</p>
                            <p className="text-sm text-muted-foreground">
                                Ask your admin to send an invite to:
                            </p>
                            <code className="block mt-2 p-2 bg-muted rounded-md text-primary font-mono text-sm max-w-sm mx-auto">
                                {user.email}
                            </code>
                            <p className="text-sm text-muted-foreground mt-4">
                                Return here once invited to complete setup.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-lg mx-auto flex flex-col space-y-8">
            <div className="flex flex-col items-center space-y-3">
                <h1 className="font-semibold text-3xl text-center">Set up your new organization</h1>
                <p className="text-center text-muted-foreground">Make your team's workspace easy to find and personalize it</p>
            </div>
            <div className="w-full max-w-2xl mx-auto space-y-8">
                <div className="space-y-3">
                    <Label htmlFor="orgName">Organization Name</Label>
                    <Input
                        id="orgName"
                        value={stepData.name}
                        onChange={(e) => handleNameChange(e.target.value)}
                        placeholder="Enter organization name"
                    />
                </div>

                <div className="space-y-3">
                    <Label htmlFor="slug">Organization URL</Label>
                    <div className="flex">
                        <div className="bg-muted px-3 py-2 border border-r-0 rounded-l-md text-muted-foreground">
                            status.bot/
                        </div>
                        <Input
                            id="slug"
                            value={stepData.slug}
                            onChange={(e) => updateStepData(stepKey, { ...stepData, slug: e.target.value })}
                            className={`rounded-l-none ${slugError ? 'border-red-500' : ''}`}
                            placeholder="your-org"
                        />
                    </div>
                    {slugError && (
                        <p className="text-sm text-red-500">{slugError}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrganizationStep; 